import {
    ArrowLeftRight,
    BarChart3,
    Banknote,
    Bell,
    FileText,
    LayoutDashboard,
    LayoutList,
    LucideIcon,
    Megaphone,
    Plug,
    Settings,
    Shield,
    ShieldCheck,
    TrendingUp,
    UserCog,
    UserPlus,
    UsersRound,
    Wallet,
} from 'lucide-react';
import { ModulePermission } from '@/types/permissions.ts';

export interface NavItem {
    /** i18n key under the `crm` namespace, e.g. `nav.customers`. */
    labelKey: string;
    /** English fallback, used if the key is missing. */
    label: string;
    path: string;
    icon: LucideIcon;
    /** VIEW on this module shows the item; `null` = always visible. */
    permission: ModulePermission | null;
    /** Visible when the user has VIEW on ANY of these (overrides `permission`). */
    anyOf?: ModulePermission[];
}

export interface NavNode {
    item: NavItem;
    /** Related pages: a hover flyout on desktop, nested rows in the drawer. */
    children: NavItem[];
}

export interface NavModel {
    main: NavNode[];
    bottom: NavNode[];
}

const item = (
    path: string,
    labelKey: string,
    label: string,
    icon: LucideIcon,
    permission: ModulePermission | null,
    anyOf?: ModulePermission[]
): NavItem => ({ path, labelKey, label, icon, permission, anyOf });

export const NAV_ITEMS: NavItem[] = [
    item('/', 'nav.dashboard', 'Dashboard', LayoutDashboard, null),
    item('/leads', 'nav.leads', 'Leads', UserPlus, ModulePermission.LEADS),
    item('/campaigns', 'nav.campaigns', 'Campaigns', Megaphone, ModulePermission.CAMPAIGNS),
    item('/customers', 'nav.customers', 'Customers', UsersRound, ModulePermission.CUSTOMERS),
    item('/kyc-requests', 'nav.kyc', 'KYC', ShieldCheck, ModulePermission.KYC),
    item('/trades', 'nav.trades', 'Trades', BarChart3, ModulePermission.TRADES),
    item(
        '/trading-accounts',
        'nav.tradingAccounts',
        'Trading Accounts',
        Wallet,
        ModulePermission.TRADING_ACCOUNTS
    ),
    item(
        '/instruments',
        'nav.instruments',
        'Instruments',
        TrendingUp,
        ModulePermission.SYSTEM_SETTINGS
    ),
    item(
        '/transactions',
        'nav.transactions',
        'Transactions',
        ArrowLeftRight,
        ModulePermission.TRANSACTIONS
    ),
    item(
        '/payment-methods',
        'nav.paymentMethods',
        'Payment Methods',
        Banknote,
        ModulePermission.PAYMENT_METHODS
    ),
    item('/users', 'nav.employees', 'Employees', UserCog, ModulePermission.USERS),
    item('/roles', 'nav.roles', 'Roles', Shield, ModulePermission.ROLES),
    item('/views', 'nav.views', 'Views', LayoutList, ModulePermission.SYSTEM_SETTINGS),
    item(
        '/integrations',
        'nav.integrations',
        'Integrations',
        Plug,
        ModulePermission.SYSTEM_SETTINGS
    ),
    item(
        '/notification-center',
        'nav.notificationCenter',
        'Notification Center',
        Bell,
        ModulePermission.NOTIFICATIONS,
        [ModulePermission.NOTIFICATIONS, ModulePermission.SYSTEM_SETTINGS]
    ),
    item(
        '/system-configuration',
        'nav.systemConfiguration',
        'System Configuration',
        Settings,
        ModulePermission.SYSTEM_SETTINGS
    ),
    item('/audit-logs', 'nav.auditLogs', 'Audit Logs', FileText, ModulePermission.SYSTEM_SETTINGS),
];

/**
 * Sidebar information architecture (same model as the Rmobile CRM shell):
 * one continuous main list where a page opens its related pages in a
 * hover flyout, and administration pinned to the bottom of the sidebar.
 */
export const MAIN_GROUPS: { path: string; children?: string[] }[] = [
    { path: '/' },
    { path: '/leads', children: ['/campaigns'] },
    { path: '/customers', children: ['/kyc-requests'] },
    { path: '/trades', children: ['/trading-accounts', '/instruments'] },
    { path: '/transactions', children: ['/payment-methods'] },
];

export const BOTTOM_GROUPS: { path: string; children?: string[] }[] = [
    { path: '/users', children: ['/roles'] },
    { path: '/notification-center' },
    { path: '/system-configuration', children: ['/views', '/integrations', '/audit-logs'] },
];

/**
 * Applies permissions to the groups. A child the user can see but whose
 * parent they cannot gets its own row instead of vanishing with it, and
 * any visible item not placed in a group is appended to the main list, so
 * a page added to NAV_ITEMS later is never silently dropped.
 */
export function buildNavModel(
    canSee: (item: NavItem) => boolean,
    items: NavItem[] = NAV_ITEMS
): NavModel {
    const visible = new Map(items.filter(canSee).map(i => [i.path, i]));
    const placed = new Set<string>();

    const resolve = (groups: { path: string; children?: string[] }[]) => {
        const nodes: NavNode[] = [];
        for (const group of groups) {
            const parent = visible.get(group.path);
            const kids = (group.children ?? [])
                .map(path => visible.get(path))
                .filter((child): child is NavItem => !!child);
            kids.forEach(child => placed.add(child.path));
            if (parent) {
                placed.add(parent.path);
                nodes.push({ item: parent, children: kids });
            } else {
                kids.forEach(child => nodes.push({ item: child, children: [] }));
            }
        }
        return nodes;
    };

    const main = resolve(MAIN_GROUPS);
    const bottom = resolve(BOTTOM_GROUPS);
    for (const [path, entry] of visible) {
        if (!placed.has(path)) main.push({ item: entry, children: [] });
    }
    return { main, bottom };
}

/** Active on its own path and on sub-paths (`/customers/42`). */
export function isPathActive(path: string, pathname: string): boolean {
    if (path === '/') return pathname === '/';
    return pathname === path || pathname.startsWith(`${path}/`);
}

/** The most specific active path among `paths`. */
export function activePathIn(paths: string[], pathname: string): string | undefined {
    return paths
        .filter(path => isPathActive(path, pathname))
        .sort((a, b) => b.length - a.length)[0];
}
